import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useLinkedIn } from "react-linkedin-login-oauth2";
import { Email, Lock } from "@mui/icons-material";
import GoogleIcon from "../../../asset/images/GoogleIcon";
import LinkedInIcon from "../../../asset/images/LinkedInIcon";
import { MESSAGING_MODE } from "../../constants/generalConstants";
import { MESSAGING_PAGE_ROUTE, MFA_VERIFY_PAGE_ROUTE, REGISTER_PAGE_ROUTE } from "../../constants/routerConstants";
import { AuthContext } from "../general/TSAuthProvider";
import { useTSData } from "../general/TSDataContext";
import TSGrid from "./TSGrid";
import config from "../../../../src/config";
import MFAPopup from "./MFAPopup";

const LoginDialog = ({ open, onClose }) => {
  const { login,mfaPending,registerMfa, verifyOtp,       
  sendOtpMail, } = useContext(AuthContext);
  const { messageActuel, mode: modeApp, setMode: setModeApp, setIsLoading } = useTSData();
  const navigate = useNavigate();

  const [email, setEmail]                     = useState("");
  const [password, setPassword]               = useState("");
  const [emailError, setEmailError]           = useState("");
  const [passwordError, setPasswordError]     = useState("");
  const [isLoggingIn, setIsLoggingIn]         = useState(false);
  const [captchaError, setCaptchaError]       = useState(false);
  const [captchaResponse, setCaptchaResponse] = useState(null);
  const [showMfaPopup, setShowMfaPopup] = useState(false);
  const recaptchaRef = useRef(null);
  const [codeOTP, setCodeOTP] = useState("");
  const [mfaError, setMfaError] = useState("");

  const validateEmail = (v) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v);

  const resetCaptcha = () => {
    recaptchaRef.current?.reset();
    setCaptchaResponse(null);
    setCaptchaError(false);
  };

  const resetForm = () => {
    setEmail(""); setPassword("");
    setEmailError(""); setPasswordError("");
    setIsLoggingIn(false);
    resetCaptcha();
  };

  const handleRegisterClick = () => {
  if (!mfaPending) return;
  setMfaError("");
  registerMfa({
    email:    mfaPending.email,
    password: mfaPending.password,
    secret:   mfaPending.secret,     
    otp:      codeOTP,
    enableMailMfa: true,            
    onSuccess: () => {
      setShowMfaPopup(false);
      setModeApp(MESSAGING_MODE);
      setCodeOTP("");
      navigate(MESSAGING_PAGE_ROUTE);
      onClose();
    },
    onFail: (msg) => setMfaError(msg || "Code OTP invalide"),
    });
    };

  // ── Social logins ──────────────────────────────────────────────────────────
  const { linkedInLogin } = useLinkedIn({
    clientId:    config.linkedInClientId,
    redirectUri: encodeURIComponent(`${window.location.origin}/linkedin`),
    state:       config.linkedInState,
    scope:       "profile email openid w_member_social",
    onSuccess: (code) => {
      setIsLoading(true);
      login({
        code, source: "linkedIn",
        onSuccess: () => { setIsLoading(false); setModeApp(MESSAGING_MODE); navigate(MESSAGING_PAGE_ROUTE); onClose(); },
        onFail:    () => setIsLoading(false),
      });
    },
    onError: (e) => console.log(e),
  });

  useEffect(() => {
    window.fbAsyncInit = function () {
      window.FB.init({ appId: "3137539656383831", cookie: true, xfbml: true, version: "v21.0" });
    };
    (function (d, s, id) {
      let js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    })(document, "script", "facebook-jssdk");
  }, []);

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (r) => {
      if (!r.access_token) return;
      setIsLoading(true);
      login({
        token: r.access_token, source: "google",
        onSuccess: () => { setIsLoading(false); setModeApp(MESSAGING_MODE); navigate(MESSAGING_PAGE_ROUTE); onClose(); },
        onFail:    () => setIsLoading(false),
      });
    },
    onError: (e) => console.error(e),
    accessType: "offline",
  });

  // ── Basic login ────────────────────────────────────────────────────────────
  const handleEmailLogin = () => {
    setEmailError(""); setPasswordError("");
    if (!email)                { setEmailError("L'email est requis");            return; }
    if (!validateEmail(email)) { setEmailError("Format d'email invalide");       return; }
    if (!password)             { setPasswordError("Le mot de passe est requis"); return; }
    if (!captchaResponse)      { setCaptchaError(true);                          return; }

    setIsLoggingIn(true);

    login({
      email, password, captchaResponse,
      source: "basic",

      onSuccess: () => {
        setIsLoggingIn(false);
        setModeApp(MESSAGING_MODE);
        navigate(MESSAGING_PAGE_ROUTE);
        onClose();
        resetForm();
      //  checkAndAdviseMfa("login");
      },


      onMfaRequired: (type) => {
      setIsLoggingIn(false);
      if (type === "enroll") {
        setShowMfaPopup(true);
      } else {
        onClose();
        resetForm();
        navigate(MFA_VERIFY_PAGE_ROUTE);
      }
    },
  

      onFail: () => {
        setIsLoggingIn(false);
        setPasswordError("Email ou mot de passe incorrect");
        resetCaptcha();
      },
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
    <Dialog
      open={open}
      onClose={() => { resetForm(); onClose(); }}
      sx={{ "& .MuiDialog-paper": { borderRadius: "30px", maxWidth: "450px" } }}
    >
      <DialogTitle>
        <TSGrid container>
          <TSGrid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Typography variant="tsh4" sx={{ fontSize: "30px", lineHeight: "45px", color: "#1B738A" }}>
              Se connecter
            </Typography>
          </TSGrid>
          <TSGrid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Typography variant="bmb1" sx={{ textAlign: "center" }}>
              Connectez-vous pour obtenir des réponses plus intelligentes.
            </Typography>
          </TSGrid>
        </TSGrid>
      </DialogTitle>

      <DialogContent sx={{ padding: "0px", overflowX: "hidden", paddingBottom: "40px" }}>
        <TSGrid container spacing={2} mt="1px">
          <TSGrid item xs={12} sx={{ paddingTop: "10px" }}>
            <TSGrid container sx={{ justifyContent: "center", ml: "13%" }} spacing={2}>

              <TSGrid item xs={12}>
                <TextField
                  label="Email" type="email" value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()}
                  error={!!emailError} helperText={emailError}
                  sx={{ mb: 1, width: "70%" }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: "gray" }} /></InputAdornment> }}
                />
              </TSGrid>

              <TSGrid item xs={12}>
                <TextField
                  label="Mot de passe" type="password" value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()}
                  error={!!passwordError} helperText={passwordError}
                  sx={{ mb: 1, width: "70%" }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: "gray" }} /></InputAdornment> }}
                />
              </TSGrid>

              <TSGrid item xs={12}>
                <FormControl error={!!captchaError} sx={{ display: "flex", flexDirection: "column", width: "70%" }}>
                  <ReCAPTCHA ref={recaptchaRef} sitekey={config.recaptchaSiteKey}
                    onChange={(v) => { setCaptchaError(false); setCaptchaResponse(v); }} />
                  {!!captchaError && <FormHelperText error>Veuillez confirmer que vous n'êtes pas un robot</FormHelperText>}
                </FormControl>
              </TSGrid>

              <TSGrid item xs={12}>
                <Button variant="contained" onClick={handleEmailLogin} disabled={isLoggingIn}
                  sx={{ backgroundColor: "#1B738A", "&:hover": { backgroundColor: "#155f73" }, "&:disabled": { backgroundColor: "#cccccc" }, borderRadius: "10px", padding: "12px", mb: 1, width: "70%" }}>
                  {isLoggingIn ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Se connecter"}
                </Button>
              </TSGrid>

              <TSGrid item xs={12}>
                <Box sx={{ width: "70%", textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    Vous n'avez pas de compte ?{" "}
                    <Link href="#" onClick={(e) => { e.preventDefault(); navigate(REGISTER_PAGE_ROUTE, { state: { email } }); onClose(); resetForm(); }}
                      sx={{ color: "#1B738A", textDecoration: "underline", cursor: "pointer" }}>
                      S'inscrire
                    </Link>
                  </Typography>
                </Box>
              </TSGrid>

              <TSGrid item xs={12} sx={{ textAlign: "center" }}>
                <Divider sx={{ width: "65%" }}><Typography variant="body2" sx={{ color: "gray" }}>ou</Typography></Divider>
              </TSGrid>

              <TSGrid item xs={12}>
                <Box sx={{ display: "flex", width: "65%", border: "2px solid black", padding: "10px", borderRadius: "10px", cursor: "pointer" }}
                  onClick={() => loginWithGoogle()}>
                  <GoogleIcon width={24} height={24} />
                  <Typography variant="tsb1" sx={{ ml: 2, alignContent: "center" }}>Se connecter avec Google</Typography>
                </Box>
              </TSGrid>

              <TSGrid item xs={12}>
                <Box sx={{ display: "flex", width: "65%", border: "2px solid black", padding: "10px", borderRadius: "10px", cursor: "pointer" }}
                  onClick={linkedInLogin}>
                  <LinkedInIcon width={24} height={24} />
                  <Typography variant="bmb1" sx={{ ml: 2, alignContent: "center" }}>Se connecter avec LinkedIn</Typography>
                </Box>
              </TSGrid>

            </TSGrid>
          </TSGrid>

          <TSGrid item xs={12} sx={{ ml: "10%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", paddingLeft: "20px", width: "75%" }}>
              <Typography variant="caption" sx={{ color: "RGB(0,0,0)", fontSize: "10px" }}>
                « Par le biais de ce formulaire, PORTNET collecte vos données personnelles pour la finalité de gestion de clients.
                Ce traitement a fait l'objet d'une déclaration auprès de la CNDP sous le numéro XXXXX.
              </Typography>
              <Typography variant="caption" sx={{ color: "RGB(0,0,0)", fontSize: "10px", mt: 1 }}>
                Vous pouvez vous adresser à <Link href="mailto:deontologie@portnet.ma">deontologie@portnet.ma</Link> pour exercer vos droits d'accès, de rectification et d'opposition conformément aux dispositions de la loi 09-08 »
              </Typography>
              <Typography variant="caption" sx={{ color: "RGB(0,0,0)", fontSize: "10px", mt: 1 }}>
                En utilisant cette application, cela signifie que vous acceptez les{" "}
                <Link href={config.baseURL + "api/referential/CGU_TIJARIA.pdf"} target="_blank">conditions générales d'utilisation</Link>.
              </Typography>
            </Box>
          </TSGrid>

          <TSGrid item xs={12} sx={{ textAlign: "center" }}>
            <Button variant="text" onClick={() => { if (modeApp !== MESSAGING_MODE && !!messageActuel) navigate(MESSAGING_PAGE_ROUTE); resetForm(); onClose(); }}>
              <Typography variant="tsb1" sx={{ color: "#1B738A", textDecorationLine: "underline" }}>Rester en mode invité</Typography>
            </Button>
          </TSGrid>

        </TSGrid>
      </DialogContent>
    </Dialog>
      <MFAPopup
        open={showMfaPopup}
        onClose={() => setShowMfaPopup(false)}
        qrCodeBase64={mfaPending?.qrCodeUrl}
        secretCode={mfaPending?.secret}
        codeOTP={codeOTP}
        setCodeOTP={setCodeOTP}
        handleRegisterClick={handleRegisterClick}
        mfaErrorMessage={mfaError}
      />
      </>
  );
};

export default LoginDialog;
